import { createDirectoryHandleFs } from "./directoryHandleFs.js";
import { GIT_GITDIR, GIT_WORKDIR } from "./gitConstants.js";
import { inferLanguageModeFromFilename } from "../../doc/inferLanguageFromFilename.js";
import { monacoLanguageId } from "../../editor/monacoLanguageId.js";
import type { DiffEditorSnapshot } from "../../ui/diffTabMonaco.js";
import { gitWorkspaceFetchStatusMatrix } from "./gitWorkspaceOperations.js";
import type { GitTerminalRunResult } from "./gitTerminalShell.js";

/** Maximum number of compare tabs that can be opened in a single `git diff` batch. */
export const GIT_DIFF_MAX_SNAPSHOTS = 30;

function decodeUtf8Loose(bytes: Uint8Array | undefined | null): string {
  if (!bytes || bytes.length === 0) return "";
  return new TextDecoder("utf-8", { fatal: false }).decode(bytes);
}

/** Normalize repo-relative path (posix, trim ./). */
export function normalizeGitDiffPath(p: string): string {
  const s = p.replace(/\\/g, "/").trim();
  if (!s || s === ".") return "";
  return s.replace(/^\.\/+/, "").replace(/^\/+/, "");
}

export type ParsedGitDiffCommand =
  | { ok: false; stderr: string }
  | { ok: true; mode: "unstaged" | "staged" | "refWorktree"; headRef: string; paths: string[] };

/**
 * Parse arguments after `git diff` (argv slice from index 2).
 * Supports: `git diff`, `git diff --cached`, `git diff HEAD`, `git diff -- path`, and benign ignored flags.
 */
export function parseGitDiffRest(rest: string[]): ParsedGitDiffCommand {
  let staged = false;
  const pos: string[] = [];
  let i = 0;
  while (i < rest.length) {
    const a = rest[i]!;
    if (a === "--") {
      i++;
      while (i < rest.length) pos.push(rest[i++]!);
      break;
    }
    if (a === "--cached" || a === "--staged") {
      staged = true;
      i++;
      continue;
    }
    if (a === "--no-color" || a === "--color=never" || a === "--color=false") {
      i++;
      continue;
    }
    if (a === "--stat" || a.startsWith("--stat=") || a === "--numstat" || a === "--shortstat") {
      i++;
      continue;
    }
    if (a === "-w" || a === "--ignore-all-space") {
      i++;
      continue;
    }
    if (/^-U\d+$/.test(a) || a.startsWith("--unified=")) {
      i++;
      continue;
    }
    if (a.startsWith("-") && a.length > 1) {
      return { ok: false, stderr: `unsupported git diff option: ${a}` };
    }
    pos.push(a);
    i++;
  }

  if (staged) {
    return { ok: true, mode: "staged", headRef: "HEAD", paths: pos.map(normalizeGitDiffPath).filter(Boolean) };
  }

  if (pos.length >= 1 && pos[0] === "HEAD") {
    const tail = pos.slice(1).map(normalizeGitDiffPath).filter(Boolean);
    return { ok: true, mode: "refWorktree", headRef: "HEAD", paths: tail };
  }

  return { ok: true, mode: "unstaged", headRef: "HEAD", paths: pos.map(normalizeGitDiffPath).filter(Boolean) };
}

const FILE = 0;
const HEAD = 1;
const WD = 2;
const ST = 3;

function unstagedDiffPaths(matrix: Array<Array<string | number>>): string[] {
  return matrix.filter((r) => Number(r[WD]) !== Number(r[ST])).map((r) => String(r[FILE]));
}

function stagedDiffPaths(matrix: Array<Array<string | number>>): string[] {
  return matrix.filter((r) => Number(r[HEAD]) !== Number(r[ST])).map((r) => String(r[FILE]));
}

function headWorkdirDiffPaths(matrix: Array<Array<string | number>>): string[] {
  return matrix.filter((r) => Number(r[HEAD]) !== Number(r[WD])).map((r) => String(r[FILE]));
}

async function walkUnstagedPair(
  root: FileSystemDirectoryHandle,
  filepath: string,
): Promise<{ left: string; right: string; found: boolean }> {
  const fs = createDirectoryHandleFs(root);
  const git = await import("isomorphic-git");
  const out = { left: "", right: "", found: false };
  await git.walk({
    fs,
    dir: GIT_WORKDIR,
    gitdir: GIT_GITDIR,
    trees: [git.STAGE(), git.WORKDIR()],
    map: async (fp, [stage, wd]) => {
      if (fp !== filepath) return null;
      out.found = true;
      if (stage) {
        const oid = await stage.oid();
        if (oid) {
          const o = await git.readObject({
            fs,
            dir: GIT_WORKDIR,
            gitdir: GIT_GITDIR,
            oid,
            format: "content",
          });
          if (o.type === "blob") out.left = decodeUtf8Loose(o.object as Uint8Array);
        }
      }
      if (wd) {
        const t = await wd.type();
        if (t === "blob") {
          const c = await wd.content();
          out.right = decodeUtf8Loose(c as Uint8Array);
        }
      }
      return true;
    },
  });
  return out;
}

async function walkStagedPair(
  root: FileSystemDirectoryHandle,
  filepath: string,
): Promise<{ left: string; right: string; found: boolean }> {
  const fs = createDirectoryHandleFs(root);
  const git = await import("isomorphic-git");
  const out = { left: "", right: "", found: false };
  await git.walk({
    fs,
    dir: GIT_WORKDIR,
    gitdir: GIT_GITDIR,
    trees: [git.TREE({ ref: "HEAD" }), git.STAGE()],
    map: async (fp, [treeHead, stage]) => {
      if (fp !== filepath) return null;
      out.found = true;
      if (treeHead) {
        const t = await treeHead.type();
        if (t === "blob") {
          const c = await treeHead.content();
          out.left = decodeUtf8Loose(c as Uint8Array);
        }
      }
      if (stage) {
        const oid = await stage.oid();
        if (oid) {
          const o = await git.readObject({
            fs,
            dir: GIT_WORKDIR,
            gitdir: GIT_GITDIR,
            oid,
            format: "content",
          });
          if (o.type === "blob") out.right = decodeUtf8Loose(o.object as Uint8Array);
        }
      }
      return true;
    },
  });
  return out;
}

async function walkRefWorktreePair(
  root: FileSystemDirectoryHandle,
  headRef: string,
  filepath: string,
): Promise<{ left: string; right: string; found: boolean }> {
  const fs = createDirectoryHandleFs(root);
  const git = await import("isomorphic-git");
  const out = { left: "", right: "", found: false };
  await git.walk({
    fs,
    dir: GIT_WORKDIR,
    gitdir: GIT_GITDIR,
    trees: [git.TREE({ ref: headRef }), git.WORKDIR()],
    map: async (fp, [treeHead, wd]) => {
      if (fp !== filepath) return null;
      out.found = true;
      if (treeHead) {
        const t = await treeHead.type();
        if (t === "blob") {
          const c = await treeHead.content();
          out.left = decodeUtf8Loose(c as Uint8Array);
        }
      }
      if (wd) {
        const t = await wd.type();
        if (t === "blob") {
          const c = await wd.content();
          out.right = decodeUtf8Loose(c as Uint8Array);
        }
      }
      return true;
    },
  });
  return out;
}

function snapshotForPath(
  filepath: string,
  leftLabel: string,
  rightLabel: string,
  leftText: string,
  rightText: string,
): DiffEditorSnapshot {
  const base = filepath.split("/").pop() ?? filepath;
  const mode = inferLanguageModeFromFilename(base);
  return {
    leftLabel,
    rightLabel,
    leftText,
    rightText,
    monacoLanguage: monacoLanguageId(mode),
    highlightLanguage: mode,
  };
}

function makeLabels(
  mode: "unstaged" | "staged" | "refWorktree",
  headRef: string,
  filepath: string,
): { leftLabel: string; rightLabel: string } {
  if (mode === "unstaged") return { leftLabel: `index — ${filepath}`, rightLabel: `worktree — ${filepath}` };
  if (mode === "staged") return { leftLabel: `HEAD — ${filepath}`, rightLabel: `index — ${filepath}` };
  return { leftLabel: `${headRef} — ${filepath}`, rightLabel: `worktree — ${filepath}` };
}

async function buildSnapshotForPath(
  root: FileSystemDirectoryHandle,
  mode: "unstaged" | "staged" | "refWorktree",
  headRef: string,
  filepath: string,
): Promise<DiffEditorSnapshot | null> {
  const { leftLabel, rightLabel } = makeLabels(mode, headRef, filepath);
  let pair: { left: string; right: string; found: boolean };
  if (mode === "unstaged") {
    pair = await walkUnstagedPair(root, filepath);
  } else if (mode === "staged") {
    pair = await walkStagedPair(root, filepath);
  } else {
    pair = await walkRefWorktreePair(root, headRef, filepath);
  }
  if (!pair.found) return null;
  return snapshotForPath(filepath, leftLabel, rightLabel, pair.left, pair.right);
}

/**
 * Implements `git diff` for the in-app Git terminal.
 * - Single explicit path → opens compare tab immediately (no confirmation).
 * - Implicit all-changed or multiple explicit paths → returns snapshots[] so the
 *   terminal panel can show the "open N tabs?" confirmation widget.
 */
export async function runGitWorkspaceDiffCompare(
  root: FileSystemDirectoryHandle,
  rest: string[],
): Promise<GitTerminalRunResult> {
  const parsed = parseGitDiffRest(rest);
  if (!parsed.ok) {
    return { stdout: "", stderr: parsed.stderr, exitCode: 1 };
  }

  const { mode, headRef, paths: explicitPaths } = parsed;

  if (explicitPaths.length > 0 && explicitPaths.some((p) => p.includes("*"))) {
    return { stdout: "", stderr: "Wildcards in paths are not supported.", exitCode: 1 };
  }

  let matrix: Array<Array<string | number>>;
  try {
    matrix = await gitWorkspaceFetchStatusMatrix(root, { includeUntracked: false });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { stdout: "", stderr: msg, exitCode: 1 };
  }

  const allPickPaths =
    mode === "unstaged"
      ? unstagedDiffPaths(matrix)
      : mode === "staged"
        ? stagedDiffPaths(matrix)
        : headWorkdirDiffPaths(matrix);

  // Determine which paths to open.
  let targetPaths: string[];
  if (explicitPaths.length === 1) {
    // Single explicit path: skip the confirmation, open directly.
    targetPaths = [explicitPaths[0]!];
  } else if (explicitPaths.length > 1) {
    // Multiple explicit paths: use them all (capped), show confirmation.
    targetPaths = explicitPaths.slice(0, GIT_DIFF_MAX_SNAPSHOTS);
  } else {
    // No explicit path: use changed files from status matrix.
    if (allPickPaths.length === 0) {
      const hint =
        mode === "unstaged"
          ? "No unstaged changes (index vs worktree)."
          : mode === "staged"
            ? "No staged changes (HEAD vs index)."
            : "No differences vs HEAD in the worktree.";
      return { stdout: `${hint}\n`, exitCode: 0 };
    }
    targetPaths = allPickPaths.slice(0, GIT_DIFF_MAX_SNAPSHOTS);
  }

  // Build all snapshots.
  const snapshots: DiffEditorSnapshot[] = [];
  for (const p of targetPaths) {
    const snap = await buildSnapshotForPath(root, mode, headRef, p);
    if (snap) snapshots.push(snap);
  }

  if (snapshots.length === 0) {
    return {
      stdout: "",
      stderr: `Path not found in repository walk: ${targetPaths.join(", ")}`,
      exitCode: 1,
    };
  }

  const truncatedNote =
    allPickPaths.length > GIT_DIFF_MAX_SNAPSHOTS
      ? ` (showing ${GIT_DIFF_MAX_SNAPSHOTS} of ${allPickPaths.length} changed files)`
      : "";

  return {
    stdout: "",
    exitCode: 0,
    openDiffSnapshots: snapshots,
    openDiffSnapshotsTotalCount: allPickPaths.length,
    openDiffSnapshotsTruncatedNote: truncatedNote || undefined,
  };
}
